export type { AsyncStatus } from "@/types/api";

export const ADMIN_PAGE_SIZES = [20, 50, 100] as const;
export type AdminPageSize = (typeof ADMIN_PAGE_SIZES)[number];

export type AdminUserStatus = "active" | "disabled";

export type AdminApplicationStatus =
  | "draft_created"
  | "queued"
  | "running"
  | "autopilot_running"
  | "manual_apply_required"
  | "automation_blocked"
  | "review_ready"
  | "ready_for_user_submission"
  | "needs_review"
  | "blocked"
  | "failed"
  | "cancelled";

export type AdminApplicationTrackingStatus =
  | "not_applied"
  | "in_progress"
  | "applied_no_response"
  | "rejected"
  | "offer";

export type AdminSupportChannel = "email" | "inbox" | "bug";
export type AdminSupportStatus =
  | "open"
  | "in_progress"
  | "waiting_for_user"
  | "resolved"
  | "closed";
export type AdminSupportPriority = "normal" | "high" | "urgent";

export type AdminCommercialKind = "subscription" | "mentor_booking";
export type AdminSubscriptionStatus =
  | "free"
  | "trialing"
  | "active"
  | "past_due"
  | "scheduled_to_cancel"
  | "paused"
  | "incomplete"
  | "incomplete_expired"
  | "canceled"
  | "unpaid";
export type AdminMentorBookingStatus =
  | "payment_pending"
  | "paid"
  | "confirmed"
  | "meeting_setup_pending"
  | "checkout_failed"
  | "expired"
  | "cancelled";
export type AdminCommercialStatus = AdminSubscriptionStatus | AdminMentorBookingStatus;

/** Query fields are deliberately narrower than the backend's ownership vocabulary. */
export interface AdminListQuery {
  page?: number;
  page_size?: number;
  priority?: string;
  search?: string;
  status?: string;
  kind?: string;
}

export interface AdminOverviewCounts {
  users_total: number;
  users_active: number;
  applications_total: number;
  support_open: number | null;
  subscriptions_active: number;
  mentor_bookings_active: number;
  jobs_running: number;
}

export type AdminCountMap = Partial<Record<string, number>>;

export interface AdminOverview {
  generated_at: string;
  counts: AdminOverviewCounts;
  application_statuses: Partial<Record<AdminApplicationStatus | AdminApplicationTrackingStatus, number>>;
  support_available: boolean;
  support_statuses: Partial<Record<AdminSupportStatus, number>> | null;
  commercial_statuses: Partial<
    Record<AdminCommercialKind, Partial<Record<AdminCommercialStatus, number>>>
  >;
}

export interface AdminUser {
  id: string;
  username: string;
  email: string;
  disabled_at: string | null;
  email_verified_at: string | null;
  totp_enabled: boolean;
  created_at: string;
  last_seen_at: string | null;
}

export interface AdminApplication {
  id: string;
  user_id: string;
  company: string;
  role_title: string;
  location: string | null;
  status: AdminApplicationStatus;
  tracking_status: AdminApplicationTrackingStatus | null;
  role_status: string | null;
  portal_type: string | null;
  last_safe_step: string | null;
  outcome_reason_code: string | null;
  review_required: boolean;
  automation_blocked: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Support reads are intentionally unavailable in the v1 backend. These are the
 * redacted metadata fields reserved for the eventual 200 response; encrypted
 * request payloads and attachment contents are not part of this contract.
 */
export interface AdminSupportRequest {
  id: string;
  user_id: string;
  channel: AdminSupportChannel;
  status: AdminSupportStatus;
  priority: AdminSupportPriority;
  attachment_count: number;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  username?: string;
  email?: string;
}

interface AdminCommercialItemBase {
  user_id: string;
  username: string;
  id: string;
  status: AdminCommercialStatus;
  start_at: string | null;
  end_at: string | null;
  updated_at: string;
}

export interface AdminSubscription extends AdminCommercialItemBase {
  kind: "subscription";
  status: AdminSubscriptionStatus;
  plan_code: string;
  session_type: null;
  duration_minutes: null;
  amount: null;
  currency: null;
}

export interface AdminMentorBooking extends AdminCommercialItemBase {
  kind: "mentor_booking";
  status: AdminMentorBookingStatus;
  plan_code: null;
  session_type: string;
  start_at: string;
  end_at: string;
  duration_minutes: number;
  amount: number;
  currency: string;
}

export type AdminCommercialItem = AdminSubscription | AdminMentorBooking;
export type AdminSubscriptionItem = AdminSubscription;
export type AdminMentorBookingItem = AdminMentorBooking;

export interface AdminPage<T> {
  items: T[];
  page: number;
  page_size: AdminPageSize;
  total: number;
  total_pages: number;
}
